import { useNotification, useProducts } from '@gozoki/api'
import { Box, Button, Grid, Paper, Switch, TextField, Typography } from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { Copy } from 'lucide-react'
import z from 'zod/v3'
import { api } from '~/lib/tuyau'
import { AuthorizedButtonLink, withRoutePermissions } from '~/utils/rbac'
import Page from '../../../components/Page'
import { fonts } from '../../../utils/theme/fonts.theme'
import { ButtonOverrides } from '../../../utils/theme/overrides.theme'

export const Route = createFileRoute('/_authenticated/notifications/$id')({
  staticData: {
    permission: ['read', 'notifications'],
  },
  component: NotificationDetailsPage,
  params: {
    parse: z.object({ id: z.coerce.number() }).parse,
  },
})
withRoutePermissions(Route)

function NotificationDetailsPage() {
  const navigate = Route.useNavigate()
  const { id } = Route.useParams()

  const { data: products } = useProducts()
  const { data: stores } = useQuery(api.stores.index.queryOptions({}))

  const { data: notification, isLoading } = useNotification(id)

  if (isLoading) {
    return null
  }
  if (!notification) {
    throw new Error('This notification does not exist')
  }

  const store = stores?.find((s) => s.id === notification.shopIds[0])

  const [action, productId] = notification.data?.path?.split('/') ?? [undefined, undefined]
  const product = productId ? products?.find((p) => p.id === Number(productId)) : undefined

  return (
    <Page>
      <div style={{ display: 'flex', paddingLeft: 10 }}>
        <Typography style={fonts.pageTitle} flexGrow={1}>
          Envoyer une notification
        </Typography>

        <Button
          variant="outlined"
          sx={{ marginRight: '16px', ...ButtonOverrides.blackOutlined }}
          onClick={() => navigate({ to: '/notifications' })}
        >
          Retour
        </Button>

        <AuthorizedButtonLink
          variant="contained"
          color="secondary"
          to="/notifications/new"
          search={{ notificationId: id }}
        >
          <Copy />
          Dupliquer
        </AuthorizedButtonLink>
      </div>

      <Paper
        sx={{
          marginTop: '16px',
          padding: '24px',
        }}
      >
        <Box sx={{ flexGrow: 1 }}>
          <Grid container spacing={4}>
            <Grid item flex={1}>
              <Typography mt="24px" mb="8px" style={fonts.inputHeader}>
                Type d'utilisateur
              </Typography>
              <Box display="flex" alignItems="center">
                <Switch
                  checked={notification.onlyUsers}
                  name="Uniquement les utilisateurs inscrits"
                  disabled
                />
                <Typography>Uniquement les utilisateurs inscrits</Typography>
              </Box>
            </Grid>
            {notification.onlyUsers ? (
              <Grid item flex={1}>
                <Typography mt="24px" mb="8px" style={fonts.inputHeader}>
                  Utilisateurs du magasin
                </Typography>
                <TextField fullWidth value={store?.label} disabled />
              </Grid>
            ) : null}
          </Grid>
        </Box>
        <Box sx={{ flexGrow: 1 }}>
          <Grid container spacing={4}>
            <Grid item flex={1}>
              <Typography mt="24px" mb="8px" style={fonts.inputHeader}>
                Titre de la notification
              </Typography>
              <TextField fullWidth value={notification.title} disabled />
            </Grid>
            <Grid item flex={1}>
              <Typography mt="24px" mb="8px" style={fonts.inputHeader}>
                Sous-titre (pour IOS uniquement)
              </Typography>
              <TextField fullWidth value={notification.subTitle} disabled />
            </Grid>
          </Grid>
        </Box>
        <Box sx={{ flexGrow: 1 }}>
          <Grid container spacing={4}>
            <Grid item flex={1}>
              <Typography mt="24px" mb="8px" style={fonts.inputHeader}>
                Message
              </Typography>
              <TextField
                fullWidth
                value={notification.text}
                disabled
                InputProps={{
                  style: {
                    alignItems: 'flex-start',
                    minHeight: '102px',
                    textAlign: 'end',
                  },
                }}
              />
            </Grid>
          </Grid>
        </Box>
        <Box sx={{ flexGrow: 1 }}>
          <Grid container spacing={4}>
            <Grid item flex={1}>
              <Typography mt="24px" mb="8px" style={fonts.inputHeader}>
                Action
              </Typography>
              <TextField fullWidth value={action ?? 'Aucune action'} disabled />
            </Grid>

            {action && ['product-anti-gaspi', 'product'].includes(action) ? (
              <Grid item flex={1}>
                <Typography mt="24px" mb="8px" style={fonts.inputHeader}>
                  Sélectionner le produit
                </Typography>
                <TextField fullWidth value={product?.label} disabled />
              </Grid>
            ) : null}
          </Grid>
        </Box>
      </Paper>
    </Page>
  )
}
